import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAthleteMode } from "./AthleteModeContext";
import { downloadAthleteContract, fetchAthleteDeal, requestContractChanges, respondToOffer, signContract } from "./athleteServices";
import { contractChecklist, fmtMoney } from "./athleteUtils";
import { fmtDateTime, fmtLongRange } from "../../ForBrands/brandDealUtils";
import DeclineModal from "./DeclineModal";
import {
  ActionBar,
  BackLink,
  Badge,
  BmModal,
  Btn,
  Card,
  Checkbox,
  ErrorState,
  Field,
  InsightBox,
  Loading,
  Overline,
  TextArea,
} from "../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import ContractMobile from "./mobile/ContractMobile";
import { DealScreen } from "./mobile/OfferMobile";

// Athlete Mode · contract review (Figma "Contract review"): agreed terms,
// signature progress (brand, athlete, guardian for minors), the scope of
// work, the fairness review and checklist, then Sign / Request changes /
// Decline. Minors: signing sends the co-sign invitation to the guardian.
// On phones (inside the Athlete Mode shell, below 640px) the page renders
// ContractMobile instead (Figma 2000:1011) with the same state and handlers;
// both modals open as bottom sheets there.

const SIGN_BADGE = {
  signed: { label: "Signed ✓", dot: "#1f9d55" },
  awaiting: { label: "Awaiting Signature", dot: "#edd200" },
  cosign: { label: "Awaiting Co-sign", dot: "#edd200" },
};

const SignatureRow = ({ name, state }) => (
  <div className="flex items-center justify-between gap-3 border-b border-bm-border py-4 last:border-b-0">
    <span className="flex items-center gap-3">
      <span className="h-8 w-8 rounded-full bg-bm-mutedbg" />
      <span className="bm-label-l text-bm-ink">{name}</span>
    </span>
    <Badge tone="outline" dot={SIGN_BADGE[state].dot}>
      {SIGN_BADGE[state].label}
    </Badge>
  </div>
);

const TermRow = ({ label, children }) => (
  <div className="flex items-start justify-between gap-6 border-b border-bm-border py-3 last:border-b-0">
    <span className="shrink-0 bm-body-m text-bm-muted">{label}</span>
    <span className="text-right bm-label-l text-bm-ink">{children}</span>
  </div>
);

const RequestChangesModal = ({ onClose, onConfirm, busy }) => {
  const [note, setNote] = useState("");
  const phone = usePhoneTheme();
  return (
    <BmModal
      title="Request changes"
      onClose={onClose}
      className={phone ? "bm-phone-form" : undefined}
      footer={
        <>
          <Btn variant="secondary" className="flex-1" onClick={onClose}>
            Cancel
          </Btn>
          <Btn className="flex-1" disabled={busy || !note.trim()} onClick={() => onConfirm(note.trim())}>
            {busy ? "Sending..." : "Send to brand"}
          </Btn>
        </>
      }
    >
      <p className="bm-body-m text-bm-muted">
        Tell the brand what needs to change before you sign. This goes to your deal thread and their inbox.
      </p>
      <Field label="What should change?" className="mt-4">
        <TextArea rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="The exclusivity clause should exclude my existing footwear partnership..." />
      </Field>
    </BmModal>
  );
};

const AthleteContractPage = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { refreshBadges } = useAthleteMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checks, setChecks] = useState({});
  const [scopeOpen, setScopeOpen] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const phone = usePhoneTheme();
  const toDeals = () => navigate("/athlete/deals");

  const load = async () => {
    try {
      const payload = await fetchAthleteDeal(dealId);
      setData(payload);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load this contract");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const checklist = useMemo(
    () => (data ? contractChecklist({ isMinor: data.athlete.isMinor, guardian: data.guardian }) : []),
    [data],
  );
  const allChecked = checklist.every((c) => checks[c.id]);

  if (error) {
    if (phone) {
      return (
        <DealScreen title="Contract review" onBack={toDeals} backLabel="Back to My Deals">
          <ErrorState>{error}</ErrorState>
        </DealScreen>
      );
    }
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) {
    if (phone) {
      return (
        <DealScreen title="Contract review" onBack={toDeals} backLabel="Back to My Deals">
          <Loading />
        </DealScreen>
      );
    }
    return <Loading />;
  }

  const { deal, campaign, brand, athlete, guardian, comparison, exclusivityText, marketRange } = data;
  const offer = deal.offer;
  const contract = deal.contract;

  if (!offer) {
    if (phone) {
      return (
        <DealScreen title="Contract review" onBack={toDeals} backLabel="Back to My Deals">
          <ErrorState>This deal has no offer attached yet.</ErrorState>
        </DealScreen>
      );
    }
    return (
      <div className="py-10">
        <ErrorState>This deal has no offer attached yet.</ErrorState>
      </div>
    );
  }

  const awaitingContract = deal.status === "accepted";
  const contractOpen = deal.status === "contract_sent";
  const athleteSigned = !!contract?.athleteSignedAt;
  const brandSigned = !!contract?.brandSignedAt;
  const guardianState = contract?.guardian ? (contract.guardian.signedAt ? "signed" : "cosign") : null;
  const canSign = contractOpen && !athleteSigned;

  const sign = async () => {
    if (!allChecked) {
      toast.error("Tick every item in the contract checklist first.");
      return;
    }
    setBusy(true);
    try {
      await signContract(dealId);
      toast.success(athlete.isMinor ? `Signed. A co-sign request went to ${guardian?.name || "your guardian"}.` : "Contract signed.");
      refreshBadges();
      await load();
    } catch (err) {
      toast.error(err.message || "Could not sign the contract");
    } finally {
      setBusy(false);
    }
  };

  const requestChanges = async (note) => {
    setBusy(true);
    try {
      const res = await requestContractChanges(dealId, note);
      toast.success("Change request sent to the brand.");
      setRequesting(false);
      navigate(`/athlete/messages?thread=${res.threadId}`);
    } catch (err) {
      toast.error(err.message || "Could not send the request");
    } finally {
      setBusy(false);
    }
  };

  // Runs once the athlete confirms in the shared DeclineModal (the same one
  // the offer view uses). A reason they type goes to the brand; without one
  // the brand gets the standard wording.
  const declineOffer = async (reason = "") => {
    const typed = String(reason || "").trim();
    setBusy(true);
    try {
      if (["offer_sent", "countered"].includes(deal.status)) {
        await respondToOffer(dealId, { action: "decline", reason: typed || "Declined at contract review." });
      } else {
        const withdraw = "I am withdrawing from this deal and will not be signing the contract.";
        await requestContractChanges(dealId, typed ? `${withdraw} Reason: ${typed}` : withdraw);
      }
      toast.success("The brand has been notified.");
      refreshBadges();
      navigate("/athlete/deals");
    } catch (err) {
      toast.error(err.message || "Could not decline");
    } finally {
      setBusy(false);
      setDeclining(false);
    }
  };

  const download = async () => {
    try {
      await downloadAthleteContract(dealId, campaign?.name || "contract");
    } catch (err) {
      toast.error(err.message || "Could not download the contract");
    }
  };

  const fairness = (() => {
    const comp = Number(offer.compensation) || 0;
    if (marketRange?.min && marketRange?.max) {
      const position = comp >= marketRange.max ? "at the top of" : comp >= marketRange.min ? "inside" : "below";
      return `We analyzed the terms. The ${fmtMoney(comp)} payout sits ${position} the ${fmtMoney(marketRange.min)}–${fmtMoney(marketRange.max)} range your NILx rates suggest for this scope. ${
        offer.exclusivityDays && offer.exclusivityDays <= 14 ? "The exclusivity window is standard." : offer.exclusivityDays ? "The exclusivity window is longer than the 14-day baseline." : "No exclusivity is attached."
      }`;
    }
    return comparison;
  })();

  const requestModal = requesting && <RequestChangesModal onClose={() => setRequesting(false)} onConfirm={requestChanges} busy={busy} />;
  const declineModal = declining && <DeclineModal onClose={() => setDeclining(false)} onConfirm={declineOffer} busy={busy} />;

  if (phone) {
    return (
      <ContractMobile
        onBack={toDeals}
        campaign={campaign}
        brand={brand}
        athlete={athlete}
        offer={offer}
        contract={contract}
        range={fmtLongRange(offer.startDate, offer.endDate)}
        subtitle={
          awaitingContract
            ? "You accepted the offer. The brand is preparing the contract; these are the agreed terms."
            : "Please review the final terms below. Once both parties sign, the deal is active."
        }
        guardianBadge={guardianState ? (guardianState === "signed" ? "Co-signed" : "Awaiting Co-sign") : null}
        guardianText={
          guardianState
            ? `Guardian signature required: A co-signer invitation has been sent to ${contract.guardian.email}.`
            : guardian
              ? `Guardian signature required: ${guardian.name} (${guardian.email}) will be invited to co-sign after you sign.`
              : "Guardian signature required: add a parent or guardian to your Circle before you can sign."
        }
        showCircleLink={!guardianState && !guardian}
        exclusivityText={exclusivityText}
        brandSigned={brandSigned}
        athleteSigned={athleteSigned}
        guardianState={guardianState}
        showGuardianRow={Boolean(contract?.guardian || athlete.isMinor)}
        guardianName={contract?.guardian?.name || guardian?.name || "Guardian"}
        fairness={fairness}
        checklist={checklist}
        checks={checks}
        onCheck={(id, v) => setChecks((s) => ({ ...s, [id]: v }))}
        statusLine={
          canSign
            ? ""
            : awaitingContract
              ? "Signing opens once the brand sends the contract."
              : athleteSigned && guardianState === "cosign"
                ? "You have signed. Waiting on your guardian's co-signature."
                : athleteSigned && !brandSigned
                  ? "You have signed. Waiting on the brand's signature."
                  : deal.status === "signed"
                    ? "Fully signed. The deal is active."
                    : ""
        }
        signed={deal.status === "signed"}
        busy={busy}
        signDisabled={busy || !canSign || (athlete.isMinor && !guardian)}
        signLabel={busy ? "Signing..." : athleteSigned ? "Signed" : "Sign contract"}
        onSign={sign}
        onOpenTracker={() => navigate(`/athlete/deals/${dealId}`)}
        onRequest={() => setRequesting(true)}
        onDecline={() => setDeclining(true)}
        onDownload={download}
        modal={
          <>
            {requestModal}
            {declineModal}
          </>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-6">
      <BackLink to="/athlete/deals">Back to My Deals</BackLink>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] bg-bm-mutedbg px-6 py-4">
        <span className="flex items-center gap-3">
          <span className="bm-h-s text-bm-ink">{campaign?.name}</span>
          <span className="h-4 w-px bg-bm-strong" />
          <span className="bm-body-m text-bm-muted">Contract review</span>
        </span>
        <span className="bm-body-m text-bm-muted">
          {brand.company} · {fmtLongRange(offer.startDate, offer.endDate)}
        </span>
      </div>

      <div className="flex flex-col gap-[6px]">
        <Overline brand>Deal flow</Overline>
        <h1 className="bm-h-l text-bm-ink">Review &amp; Sign Contract</h1>
        <p className="bm-body-l text-bm-muted">
          {awaitingContract
            ? "You accepted the offer. The brand is preparing the contract; these are the agreed terms."
            : "Please review the final terms below. Once both parties sign, the deal is active."}
        </p>
      </div>

      {athlete.isMinor && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] bg-bm-mutedbg px-5 py-[14px]">
          <span className="flex items-center gap-3 bm-label-l text-bm-ink">
            <span className="text-[16px]">⚠</span>
            {guardianState
              ? `Guardian signature required: A co-signer invitation has been sent to ${contract.guardian.email}.`
              : guardian
                ? `Guardian signature required: ${guardian.name} (${guardian.email}) will be invited to co-sign after you sign.`
                : "Guardian signature required: add a parent or guardian to your Circle before you can sign."}
          </span>
          {guardianState ? (
            <Badge tone="dark">{guardianState === "signed" ? "Co-signed" : "Awaiting Co-sign"}</Badge>
          ) : !guardian ? (
            <Link to="/athlete/circle" className="bm-label-l text-bm-orange underline underline-offset-2">
              Open My Circle
            </Link>
          ) : null}
        </div>
      )}

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-6">
          <Card className="flex flex-col gap-4">
            <h2 className="bm-h-m text-bm-ink">Agreed Deal Terms</h2>
            <div>
              <TermRow label="Compensation">{fmtMoney(offer.compensation, { cents: true })} · paid in full to you</TermRow>
              <TermRow label="Deliverables">
                <span className="flex flex-col items-end">
                  {(offer.deliverableLines || []).map((l) => (
                    <span key={l}>{l}</span>
                  ))}
                </span>
              </TermRow>
              <TermRow label="Payment schedule">
                <span className="flex flex-col items-end">
                  {(offer.milestones || []).map((m) => (
                    <span key={m.id}>
                      {fmtMoney(m.amount)} {m.label}
                    </span>
                  ))}
                </span>
              </TermRow>
              <TermRow label="Timeline">{fmtLongRange(offer.startDate, offer.endDate)}</TermRow>
              <TermRow label="Exclusivity">{exclusivityText}</TermRow>
              {offer.adUsageDays ? <TermRow label="Ad usage rights">{offer.adUsageDays} days</TermRow> : null}
            </div>
          </Card>

          <Card className="flex flex-col gap-2">
            <h2 className="bm-h-m text-bm-ink">Signature Progress</h2>
            <div>
              <SignatureRow name={`${brand.company} (Brand)`} state={brandSigned ? "signed" : "awaiting"} />
              <SignatureRow name={`${athlete.fullName} (You)`} state={athleteSigned ? "signed" : "awaiting"} />
              {(contract?.guardian || athlete.isMinor) && (
                <SignatureRow
                  name={`${contract?.guardian?.name || guardian?.name || "Guardian"} (Guardian)`}
                  state={guardianState === "signed" ? "signed" : "cosign"}
                />
              )}
            </div>
            {contract && (
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Btn variant="secondary" size="sm" onClick={download}>
                  {contract.hasFile ? "Download contract" : "Download terms"}
                </Btn>
                {contract.sentAt && <span className="bm-body-s text-bm-muted">Sent {fmtDateTime(contract.sentAt)}</span>}
              </div>
            )}
          </Card>

          <Card padding="p-0" className="overflow-hidden">
            <button type="button" onClick={() => setScopeOpen((o) => !o)} className="flex w-full items-center justify-between px-6 py-5 text-left">
              <span className="bm-h-s text-bm-ink">Scope of work</span>
              <span className={`bm-body-m text-bm-muted transition-transform ${scopeOpen ? "rotate-180" : ""}`}>⌄</span>
            </button>
            {scopeOpen && (
              <div className="bm-scroll mx-6 mb-6 max-h-[200px] overflow-y-auto rounded-[8px] bg-bm-subtle p-4">
                <pre className="whitespace-pre-wrap font-inter bm-body-s text-bm-muted">{contract?.scopeOfWork || offer.scopeOfWork || "The brand has not attached a scope of work."}</pre>
              </div>
            )}
          </Card>
        </div>

        <div className="flex w-full flex-col gap-6 lg:w-[420px]">
          <InsightBox title="Cache AI fairness review">{fairness}</InsightBox>
          <Card className="flex flex-col gap-4">
            <Overline>Contract checklist</Overline>
            {checklist.map((c) => (
              <Checkbox key={c.id} checked={!!checks[c.id]} onChange={(v) => setChecks((s) => ({ ...s, [c.id]: v }))} label={c.label} />
            ))}
            {!canSign && (
              <p className="bm-body-s text-bm-muted">
                {awaitingContract
                  ? "Signing opens once the brand sends the contract."
                  : athleteSigned && guardianState === "cosign"
                    ? "You have signed. Waiting on your guardian's co-signature."
                    : athleteSigned && !brandSigned
                      ? "You have signed. Waiting on the brand's signature."
                      : deal.status === "signed"
                        ? "Fully signed. The deal is active."
                        : ""}
              </p>
            )}
          </Card>
        </div>
      </div>

      <ActionBar
        left={
          <>
            <Btn variant="secondary" onClick={() => setRequesting(true)} disabled={busy || deal.status === "signed"}>
              Request changes
            </Btn>
            {deal.status !== "signed" && (
              <Btn variant="danger" onClick={() => setDeclining(true)} disabled={busy}>
                Decline Offer
              </Btn>
            )}
          </>
        }
        right={
          deal.status === "signed" ? (
            <Btn onClick={() => navigate(`/athlete/deals/${dealId}`)}>Open campaign tracker</Btn>
          ) : (
            <Btn onClick={sign} disabled={busy || !canSign || (athlete.isMinor && !guardian)}>
              {busy ? "Signing..." : athleteSigned ? "Signed" : "Sign contract"}
            </Btn>
          )
        }
      />

      {requestModal}
      {declineModal}
    </div>
  );
};

export default AthleteContractPage;
